import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ref, update } from 'firebase/database';
import { database } from '../../services/firebase';
import { analyzeLogicalAnswer, generateLogicalQuestion } from '../../services/geminiService';
import Swal from 'sweetalert2';

const LogicalTest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [question, setQuestion] = useState('');
  const userId = localStorage.getItem('userId');

  // ✅ Debug - Check if state is available
  useEffect(() => {
    console.log('Location State:', location.state);
  }, [location.state]);

  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        const randomQuestion = await generateLogicalQuestion();
        setQuestion(randomQuestion);
      } catch (error) {
        console.error('Error generating question:', error);
        setQuestion('Failed to load the question. Please refresh the page.');
      }
    };
    fetchQuestion();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Analyze answer using AI
      const analysis = await analyzeLogicalAnswer(answer);
      const score = analysis?.score || 0;
      const feedback = analysis?.feedback || 'No feedback available';

      // ✅ Correctly fetch companyName and jobId
      const companyName = location?.state?.companyName
        ? location.state.companyName.replace(/\s+/g, '_').toLowerCase()
        : 'default_company';
      const jobId = location?.state?.jobId || 'unknown';


      console.log(companyName);

      // Check if companyName and jobId are valid
      if (!location.state || !location.state.companyName || !location.state.jobId) {
        console.warn('Invalid companyName or jobId. Skipping notifications.');
      }

      // ✅ Prepare updates for Firebase
      const updates = {};
      const testDate = new Date().toISOString();

      // Save test results
      updates[`users/${userId}/applications/${jobId}/logicalTest`] = {
        question,
        answer,
        score,
        completedAt: testDate,
        aiAnalysis: feedback,
      };

      // Add notification for recruiter if valid
      if (companyName !== 'TechCorp' && jobId != '1') {
        const notificationId = Date.now().toString();
        updates[`notifications/${companyName}/${notificationId}`] = {
          type: 'logical_test_completed',
          userId,
          jobId,
          message: `Candidate has completed the logical test with score ${score}/100`,
          timestamp: testDate,
          status: 'unread',
        };
      }

      // ✅ Update Firebase with results
      await update(ref(database), updates);

      // Show success message
      Swal.fire({
        icon: 'success',
        title: 'Test Completed!',
        text: `Your logical reasoning test has been submitted. Score: ${score}/100`,
      }).then(() => {
        navigate('/dashboard/user');
      });
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: 'Please try again later.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="bg-white rounded-2xl shadow-2xl p-10">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Logical Reasoning Test</h1>
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Question:</h2>
          <p className="text-gray-700">{question || 'Loading question...'}</p>
        </div>
        <form onSubmit={handleSubmit}>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            className="w-full h-48 p-4 border rounded-xl mb-6"
            placeholder="Type your answer here..."
            required
          />
          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-4 rounded-xl text-lg font-semibold ${
              submitting ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'
            } text-white`}
          >
            {submitting ? 'Submitting...' : 'Submit Answer'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LogicalTest;
